import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close-2.svg';
import { ReactComponent as PanelGripIcon } from '@elementary/common-ui/assets/panel-grip.svg';
import {
  Chip,
  Flex,
  FlexCenter,
  FlexSpace,
  IconButton,
  LabelLink,
  Loading,
  Tab as TabBase,
  Tabs as TabsBase,
  TestResultsTable,
} from '@elementary/common-ui/components';
import { ResizablePanelVertical } from '@elementary/common-ui/components/ResizablePanel';
import { HEADER_HEIGHT } from '@elementary/common-ui/constants';
import useNavigateWithEnvParam from '@elementary/common-ui/hooks/useNavigateWithEnvParam';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useUrlFilters, {
  UrlFilters,
} from '@elementary/common-ui/hooks/useUrlFilters';
import useWindowSize from '@elementary/common-ui/hooks/useWindowSize';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import {
  LINEAGE_FILTERS,
  TEST_RESULTS_TABLE_FILTERS,
  TREE_NODE,
  TREE_TYPE,
} from '@elementary/common-ui/constants/urlParams';
import { TabContext, TabPanel as TabPanelBase } from '@mui/lab';
import { Drawer, Stack, Typography } from '@mui/material';
import NodeInfo from './NodeInfo';
import { ResourceType } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import { getTestResultsColumns } from './columns';
import { useInfoPanelTestResults } from './useInfoPanelTestResults';
import { useLineageNodeInfo } from './useLineageNodeInfo';

const LINEAGE_HEADER_HEIGHT: number = 57;

const DrawerWrapper = styled(Stack)`
  height: inherit;
  box-shadow: 0 2px 10px 0 ${({ theme }) => theme.palette.common.gray13};
`;

const PanelGrip = styled(FlexCenter)`
  position: absolute;
  top: -5px;
  left: calc(50% - 16px);
  width: 32px;
  height: 10px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray11};
  border-radius: 2px;
  background: ${({ theme }) => theme.palette.common.white};
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin: 0 8px;
  background-color: ${({ theme }) => theme.palette.common.black};
`;

const Header = styled(FlexSpace)<{ $isHover: boolean }>`
  position: relative;
  padding: 0 16px 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray13};
  box-shadow: ${({ theme, $isHover }) =>
    $isHover
      ? `0 -1.5px ${theme.palette.common.gray14}`
      : `0 -1px ${theme.palette.common.gray13}`};
  transition: box-shadow 0.2s;
`;

const Tabs = styled(TabsBase)`
  height: 48px;
`;

const Tab = styled(TabBase)`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.common.black};
  text-transform: uppercase;
`;

const TabPanel = styled(TabPanelBase)`
  height: 100%;
  overflow: hidden;
  padding: 16px 24px 24px 24px;
`;

const TabHeader = styled(Flex)`
  padding-bottom: 16px;
`;

const EmptyStateWrapper = styled(Stack)`
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100% - 40px);
`;

const CloseIconStyled = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const PanelTitle = ({
  label,
  id,
  hasCatalogEntry,
  catalogTreeType,
}: {
  label?: string;
  id?: string;
  hasCatalogEntry: boolean;
  catalogTreeType: 'bi_assets' | 'data_assets';
}) => {
  const { formatUrlParam } = useSelectedTreeItem();
  const navigate = useNavigateWithEnvParam();
  const newParams = new URLSearchParams();

  if (!hasCatalogEntry) {
    return (
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: (theme) => theme.typography.fontWeightBold,
        }}
      >
        {label || id}
      </Typography>
    );
  }

  return (
    <LabelLink
      onClick={() => {
        const urlParam = formatUrlParam(id);

        if (id && urlParam) {
          newParams.set(TREE_NODE, urlParam);
          newParams.set(TREE_TYPE, catalogTreeType);
          navigate(`/catalog?${newParams?.toString()}`);
        }
      }}
      size="large"
    >
      {label || id}
    </LabelLink>
  );
};

const TestsSummery = ({
  totals,
}: {
  totals?: { passed: number; warnings: number; errors: number };
}) => (
  <FlexCenter sx={{ whiteSpace: 'nowrap' }}>
    <Typography>{`${totals?.passed || 0} Passed`}</Typography>
    <Dot />
    <Typography>{`${totals?.warnings || 0} ${
      totals?.warnings === 1 ? 'Warning' : 'Warnings'
    }`}</Typography>
    <Dot />
    <Typography>{`${totals?.errors || 0} ${
      totals?.errors === 1 ? 'Failure' : 'Failures'
    }`}</Typography>
  </FlexCenter>
);

type TabProps = {
  label: string;
  order: number;
};

enum TabTypes {
  TestResultsTab = 'testResults',
  NodeInfoTab = 'nodeInfo',
}

enum ExposureTabTypes {
  NodeInfoTab = 'nodeInfo',
}

enum ColumnTabTypes {
  TestResultsTab = 'testResults',
}

const tabs: { [key in TabTypes]: TabProps } = {
  [TabTypes.TestResultsTab]: {
    label: 'Test Results',
    order: 0,
  },
  [TabTypes.NodeInfoTab]: {
    label: 'Node Info',
    order: 1,
  },
};

const exposureTabs: { [key in ExposureTabTypes]: TabProps } = {
  [ExposureTabTypes.NodeInfoTab]: {
    label: 'Node Info',
    order: 0,
  },
};

const columnNodeTabs: { [key in ColumnTabTypes]: TabProps } = {
  [TabTypes.TestResultsTab]: {
    label: 'Test Results',
    order: 0,
  },
};

type PanelHeaderProps = {
  isHeaderGripHover: boolean;
  selectedTab: number;
  handleTabChange: (_event: SyntheticEvent, newValue: number) => void;
  handleClose: () => void;
  tabs: { [key: string]: TabProps };
  hiddenTabs: TabTypes[];
};

const PanelHeader = (props: PanelHeaderProps) => {
  const {
    selectedTab,
    tabs: tabsByType,
    isHeaderGripHover,
    handleTabChange,
    handleClose,
    hiddenTabs,
  } = props;
  return (
    <Header $isHover={isHeaderGripHover}>
      <PanelGrip>
        <PanelGripIcon />
      </PanelGrip>
      <Tabs value={selectedTab} onChange={handleTabChange}>
        {Object.keys(tabsByType)
          .filter((key) => !hiddenTabs.includes(key as TabTypes))
          .map((key) => (
            <Tab
              key={key}
              label={tabsByType[key].label}
              value={tabsByType[key].order}
              wrapped
            />
          ))}
      </Tabs>
      <IconButton onClick={handleClose}>
        <CloseIconStyled />
      </IconButton>
    </Header>
  );
};

const LineageInfoPanel = () => {
  const { height: windowHeight } = useWindowSize();
  const { urlFilters } = useUrlFilters({ urlKey: LINEAGE_FILTERS });
  const { applyFilters, clearAllFilters } = useUrlFilters({
    urlKey: TEST_RESULTS_TABLE_FILTERS,
  });
  const hasPermission = useEnvPermission();

  const testsFilter = urlFilters?.find((f) => f.property === 'test');
  const statusFilter = urlFilters?.find((f) => f.property === 'status');

  const hasTestReadScope = hasPermission('VIEW_TESTS');
  const hiddenTabs = hasTestReadScope ? [] : [TabTypes.TestResultsTab];

  const initialPanelHeight = useMemo(
    () =>
      windowHeight
        ? (windowHeight - HEADER_HEIGHT - LINEAGE_HEADER_HEIGHT) * 0.4
        : 0,
    [windowHeight],
  );

  const [panelHeight, setPanelHeight] = useState<number>(initialPanelHeight);
  const [isHeaderGripHover, setIsHeaderGripHover] = useState<boolean>(false);
  const { highlightedNode, lineageInfoPanel, setLineageInfoPanel, reportData } =
    useLineageGraphContext();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [exposureSelectedTab, setExposureSelectedTab] = useState<number>(0);

  const { resource, totals, jobInvocation, hasCatalogEntry, catalogTreeType } =
    useLineageNodeInfo();

  useEffect(
    () => setSelectedTab(lineageInfoPanel?.tab === 'nodeInfo' ? 1 : 0),
    [lineageInfoPanel],
  );

  const { tableData, isFetching } = useInfoPanelTestResults(
    reportData?.test_results,
    highlightedNode?.parentId || highlightedNode?.id,
  );

  const handleClose = useCallback(() => {
    setLineageInfoPanel({ isOpen: false });
    clearAllFilters();
  }, [setLineageInfoPanel, clearAllFilters]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleExposureTabChange = (
    _event: SyntheticEvent,
    newValue: number,
  ) => {
    setExposureSelectedTab(newValue);
  };

  const statusFilterMapped: string[] = useMemo(() => {
    if (statusFilter?.value.find((status) => status === 'errors')) {
      return ['fail'];
    }
    if (statusFilter?.value.find((status) => status === 'warnings')) {
      return ['warn'];
    }
    return [];
  }, [statusFilter]);

  useEffect(() => {
    if (
      !testsFilter?.value.length &&
      !statusFilterMapped?.length &&
      !highlightedNode?.parentId
    ) {
      clearAllFilters();
      return;
    }

    const filtersArr: UrlFilters = [];

    if (testsFilter?.value.length) {
      filtersArr.push({
        property: 'testName',
        operator: 'equals',
        value: testsFilter?.value,
      });
    }

    if (statusFilterMapped?.length) {
      filtersArr.push({
        property: 'latestRunStatus',
        operator: 'equals',
        value: statusFilterMapped,
      });
    }

    if (highlightedNode?.parentId) {
      const columnName = highlightedNode.metadata?.label;

      if (columnName) {
        filtersArr.push({
          property: 'columnName',
          operator: 'equals',
          value: [columnName],
        });
      }
    }

    applyFilters(filtersArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedNode, statusFilterMapped, testsFilter]);

  const columns = useMemo(() => {
    const showTestsFilter = testsFilter?.value.length === 0;
    const showStatusFilter = statusFilter?.value.length === 0;
    return getTestResultsColumns({
      tableData,
      showTestsFilter,
      showStatusFilter,
    });
  }, [statusFilter, tableData, testsFilter]);

  const panelMaxHeight = useMemo(
    () => (windowHeight || window.innerHeight) - HEADER_HEIGHT,
    [windowHeight],
  );

  useEffect(() => {
    if (!highlightedNode) {
      setPanelHeight(initialPanelHeight);
    }
  }, [initialPanelHeight, highlightedNode, resource]);

  useEffect(() => {
    if (!highlightedNode) {
      setLineageInfoPanel({ isOpen: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedNode]);

  return (
    <>
      {lineageInfoPanel.isOpen && (
        <ResizablePanelVertical
          height={panelHeight}
          maxHeight={panelMaxHeight}
          minHeight={100}
          onDrag={(height) => {
            setPanelHeight(height);
            setIsHeaderGripHover(true);
          }}
          onStop={() => setIsHeaderGripHover(false)}
          onMouseEnter={() => setIsHeaderGripHover(true)}
          onMouseLeave={() => setIsHeaderGripHover(false)}
        />
      )}
      <Drawer
        sx={{
          '& .MuiPaper-root': {
            height: `${
              panelHeight > panelMaxHeight ? panelMaxHeight : panelHeight
            }px`,
            overflow: 'visible',
            border: 'none',
            position: 'absolute',
            zIndex: 1,
          },
        }}
        open={lineageInfoPanel.isOpen}
        anchor="bottom"
        variant="persistent"
      >
        <DrawerWrapper>
          {resource &&
            (resource?.resourceType === ResourceType.Exposure ? (
              <TabContext value={exposureSelectedTab.toString()}>
                <PanelHeader
                  selectedTab={exposureSelectedTab}
                  handleTabChange={handleExposureTabChange}
                  handleClose={handleClose}
                  isHeaderGripHover={isHeaderGripHover}
                  tabs={exposureTabs}
                  hiddenTabs={hiddenTabs}
                />
                <TabPanel
                  tabIndex={exposureTabs[ExposureTabTypes.NodeInfoTab].order}
                  value={exposureTabs[
                    ExposureTabTypes.NodeInfoTab
                  ].order.toString()}
                >
                  <TabHeader>
                    <PanelTitle
                      label={resource?.fqn}
                      id={highlightedNode?.parentId || highlightedNode?.id}
                      hasCatalogEntry={hasCatalogEntry}
                      catalogTreeType={catalogTreeType}
                    />
                  </TabHeader>
                  <NodeInfo node={resource} invocation={jobInvocation} />
                </TabPanel>
              </TabContext>
            ) : (
              <TabContext value={selectedTab.toString()}>
                <PanelHeader
                  selectedTab={
                    highlightedNode?.parentId
                      ? hasTestReadScope
                        ? columnNodeTabs[ColumnTabTypes.TestResultsTab].order
                        : tabs[TabTypes.NodeInfoTab].order
                      : selectedTab
                  }
                  handleTabChange={handleTabChange}
                  handleClose={handleClose}
                  isHeaderGripHover={isHeaderGripHover}
                  tabs={highlightedNode?.parentId ? columnNodeTabs : tabs}
                  hiddenTabs={hiddenTabs}
                />

                {hasTestReadScope && (
                  <TabPanel
                    tabIndex={tabs[TabTypes.TestResultsTab].order}
                    value={tabs[TabTypes.TestResultsTab].order.toString()}
                  >
                    <TabHeader gap={3}>
                      <PanelTitle
                        label={resource?.fqn}
                        id={highlightedNode?.parentId || highlightedNode?.id}
                        hasCatalogEntry={hasCatalogEntry}
                        catalogTreeType={catalogTreeType}
                      />
                      <TestsSummery totals={totals} />
                    </TabHeader>

                    {isFetching ? (
                      <Loading />
                    ) : (
                      <TestResultsTable
                        sx={{ pb: 2 }}
                        wrapperSx={{
                          maxHeight: '100%',
                        }}
                        data={tableData}
                        columns={columns}
                        emptyStateRenderer={() => (
                          <EmptyStateWrapper>
                            <Chip label="No test executions" color="warning" />
                          </EmptyStateWrapper>
                        )}
                        localStorageKeyPrefix="lineage"
                      />
                    )}
                  </TabPanel>
                )}
                <TabPanel
                  tabIndex={tabs[TabTypes.NodeInfoTab].order}
                  value={tabs[TabTypes.NodeInfoTab].order.toString()}
                >
                  <TabHeader>
                    <PanelTitle
                      label={resource?.fqn}
                      id={highlightedNode?.parentId || highlightedNode?.id}
                      hasCatalogEntry={hasCatalogEntry}
                      catalogTreeType={catalogTreeType}
                    />
                  </TabHeader>
                  <NodeInfo node={resource} invocation={jobInvocation} />
                </TabPanel>
              </TabContext>
            ))}
        </DrawerWrapper>
      </Drawer>
    </>
  );
};

export default LineageInfoPanel;
